import { useState, useCallback, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export type DemoTier = "starter_demo" | "professional_demo" | "agency_demo";

/** Maps demo tiers to the real subscription tier they mirror */
const DEMO_TIER_MAP: Record<DemoTier, string> = {
  starter_demo: "starter",
  professional_demo: "professional",
  agency_demo: "agency",
};

/** Reverse: real tier → demo tier */
const REAL_TO_DEMO: Record<string, DemoTier> = {
  starter: "starter_demo",
  professional: "professional_demo",
  agency: "agency_demo",
};

/** Which real tier a feature belongs to */
export type FeatureTier = "professional" | "agency";

export function useDemoMode() {
  const { roles, profile, user, refreshProfile } = useAuth();
  const isDemoUser = roles.includes("demo_user" as any);

  // Derive demo tier from profile's actual subscription_tier in DB
  const profileTier = profile?.subscription_tier ?? "starter";
  const initialDemoTier: DemoTier = REAL_TO_DEMO[profileTier] || "starter_demo";

  const [demoTier, setDemoTier] = useState<DemoTier>(initialDemoTier);

  // Sync from profile when it changes
  useEffect(() => {
    if (isDemoUser && profile) {
      const mapped = REAL_TO_DEMO[profile.subscription_tier] || "starter_demo";
      setDemoTier(mapped);
    }
  }, [isDemoUser, profile?.subscription_tier]);

  const effectiveTier = isDemoUser ? DEMO_TIER_MAP[demoTier] : null;

  /** Persist tier change to the database profile */
  const persistTier = useCallback(
    async (realTier: string) => {
      if (!user) return;
      await supabase
        .from("profiles")
        .update({
          subscription_tier: realTier as any,
          subscription_active: true,
        })
        .eq("user_id", user.id);
      await refreshProfile();
    },
    [user, refreshProfile]
  );

  /** Check if user has access to a feature tier level */
  const hasAccess = useCallback(
    (requiredTier: FeatureTier): boolean => {
      if (!isDemoUser) return false;
      const tierOrder: string[] = ["starter", "professional", "agency"];
      const currentIdx = tierOrder.indexOf(DEMO_TIER_MAP[demoTier]);
      const requiredIdx = tierOrder.indexOf(requiredTier);
      return currentIdx >= requiredIdx;
    },
    [isDemoUser, demoTier]
  );

  /** Auto-upgrade when a locked feature is clicked */
  const unlockFeature = useCallback(
    async (requiredTier: FeatureTier) => {
      if (!isDemoUser) return;
      const newDemoTier: DemoTier = requiredTier === "agency" ? "agency_demo" : "professional_demo";
      const realTier = DEMO_TIER_MAP[newDemoTier];
      setDemoTier(newDemoTier);
      await persistTier(realTier);
      const planName = requiredTier.charAt(0).toUpperCase() + requiredTier.slice(1);
      toast({
        title: `🔓 Unlocked for demo`,
        description: `This feature is included in the ${planName} plan.`,
      });
    },
    [isDemoUser, persistTier]
  );

  /** Reset to starter demo — persists to DB */
  const resetDemo = useCallback(async () => {
    setDemoTier("starter_demo");
    await persistTier("starter");
    toast({
      title: "Demo Reset",
      description: "Demo mode has been reset to Starter plan.",
    });
  }, [persistTier]);

  return {
    isDemoUser,
    demoTier,
    effectiveTier,
    hasAccess,
    unlockFeature,
    resetDemo,
  };
}
